















































































































































































import { Component, Vue, Ref, Watch } from "vue-property-decorator";
import store from "@/store";
import moment from "moment";
//  @types
import { Header, Row } from "@/types/VDataTable";
import { Company } from "@/types/company";
//  @components
import { headers } from "@/components/Settings/CompanyList/headers";
import SortableArrows from "@/components/SortableArrows.vue";
import DataCell from "./DataCell.vue";
import Tooltip from "@/components/Tooltip.vue";
import ConfirmDialog from "@/components/ConfirmDialog.vue";
import CompanyDataModal from "./CompanyDataModal.vue";

// @clients
import CompaniesClient from "Clients/companies-client";

//  @modules
import { getModule } from "vuex-module-decorators";
import CompaniesModule from "@/store/clients/Companies.module";
import UsersModule from "@/store/clients/Users.module";
import SnackbarModule from "@/store/clients/Snackbar.module";

const Companies = getModule(CompaniesModule, store);
const Users = getModule(UsersModule, store);
const Snackbar = getModule(SnackbarModule, store);

@Component({
  components: {
    SortableArrows,
    DataCell,
    Tooltip,
    ConfirmDialog,
    CompanyDataModal,
  },
})
export default class DataTable extends Vue {
  @Ref("confirm") confirm!: any;
  // @Data
  loading = false;
  isLoadingTextDisplay = false;
  sortDesc = false;
  searchQuery = "";
  headers: Header[] = headers;
  sortBy = "";
  pageLimit = -1; // -1 show all items by default
  pageCount = 0;
  currentItems: Row[] = [];
  currentPage = 1;
  selectedCompany: Company | null = null;
  CompanyDataModal = false;

  @Watch("CompanyDataModal")
  onCompanyDataModalClose(newVal: boolean) {
    if (!newVal) this.selectedCompany = null;
  }

  // @Getters
  get isLessThanLimit(): boolean {
    return this.rows.length <= this.pageLimit;
  }

  get skeletonStructure(): object {
    return { "table-row": `table-cell@5` };
  }

  get filteredHeaders(): Header[] {
    const result = this.headers.filter(header => {
      return true;
    });
    return result;
  }

  get rows(): any {
    let rows = this.companies.map((c, index) => {
      return {
        index: index,
        companyId: c.id,
        companyName: {
          value: c.name,
        },
        createdDate: {
          value: c.createdDate ? moment.utc(c.createdDate).format("DD. MMM, YYYY") : "",
          subtitle: c.createdDate ? moment.utc(c.createdDate).fromNow() : "",
        },
        vesselCount: {
          value: c.vesselCount,
        },
        subscriptionStatus: {
          value: c.subscriptionStatus,
        },
        actions: {
          value: ["viewascompany", "edit", "delete"],
        },
      };
    });

    // Sort companies by name alphabetically
    rows = rows.sort((a, b) => {
      const nameA = (a.companyName.value || "").toLowerCase();
      const nameB = (b.companyName.value || "").toLowerCase();

      return nameA.localeCompare(nameB);
    });

    // Filter by search query
    if (this.searchQuery && this.searchQuery.trim()) {
      const query = this.searchQuery.toLowerCase();
      rows = rows.filter(item => {
        // Search by user name or email number
        const nameMatch = item.companyName.value ? item.companyName.value.toLowerCase().includes(query) : false;
        return nameMatch;
      });
    }

    return rows;
  }

  get sortedRows() {
    if (!this.sortBy) return this.rows;

    return this.rows.slice().sort((a: any, b: any) => {
      // Special handling for Last Activity Date column
      if (this.sortBy === "createdDate") {
        const dateA = new Date(a[this.sortBy]?.value ?? "");
        const dateB = new Date(b[this.sortBy]?.value ?? "");
        if (this.sortDesc) {
          return dateB.getTime() - dateA.getTime();
        } else {
          return dateA.getTime() - dateB.getTime();
        }
      }

      // Default string/other sorting
      const valueA = a[this.sortBy]?.value ?? "";
      const valueB = b[this.sortBy]?.value ?? "";

      // Normalize values for comparison
      const normalizedA = typeof valueA === "string" ? valueA.toLowerCase() : valueA;
      const normalizedB = typeof valueB === "string" ? valueB.toLowerCase() : valueB;

      if (this.sortDesc) {
        return normalizedA < normalizedB ? 1 : normalizedA > normalizedB ? -1 : 0;
      } else {
        return normalizedA > normalizedB ? 1 : normalizedA < normalizedB ? -1 : 0;
      }
    });
  }

  get companies(): Company[] {
    return Companies.companies;
  }

  // @Methods
  cellData(row: Row, header: Header, index: number): any {
    return {
      column: header.value,
      columnName: header.text,
      companyId: row.companyId,
      type: header.type,
      align: header.align,
      value: row[header.value].value,
      subtitle: row[header.value].subtitle,
      link: row[header.value].link,
    };
  }

  async initializeTable(): Promise<void> {
    try {
      this.loading = true;
      this.isLoadingTextDisplay = false;
      await Companies.refreshCompanies();

      // Show loading text if data takes more than 2 seconds to load
      setTimeout(() => {
        if (this.loading) {
          this.isLoadingTextDisplay = true;
        }
      }, 2000);
    } catch (error) {
      Snackbar.showSnackbar({ text: "Failed to load companies data" });
    } finally {
      this.loading = false;
      this.isLoadingTextDisplay = false;
    }
  }

  async onSubmitCompanyData(payload: { companyData: Company; isNewCompany: boolean }): Promise<void> {
    if (payload.isNewCompany) this.searchQuery = payload.companyData.name ?? "";
    try {
      this.loading = true;
      this.isLoadingTextDisplay = true;
      await Companies.refreshCompanies();
      await this.initializeTable();
    } catch (error) {
      Snackbar.showSnackbar({ text: "Failed to load companies data after adding/editing company" });
    } finally {
      this.loading = false;
      this.isLoadingTextDisplay = false;
    }
  }

  sort(value: string): void {
    if (this.sortBy === value) {
      this.sortDesc = !this.sortDesc; // Toggle sort direction
      if (!this.sortDesc) this.sortBy = ""; // Reset sorting if toggled off
    } else {
      this.sortBy = value;
      this.sortDesc = false; // Default to ascending order
    }
  }

  updateSortBy(value: string): void {
    if (!value) value = "";
  }

  updateSortDesc(value: boolean): void {
    if (!value) value = false;
  }

  updateItemsPerPage(value: number): void {
    if (!value) value = 20;
    this.pageLimit = value;
  }

  headerClasses(header: any): string[] {
    return [
      header.class,
      {
        sortable: header.sortable,
        "no-sort": this.sortBy !== header.value,
      },
    ];
  }

  headerStyles(header: any): any {
    return {
      width: header.width ? header.width : "auto",
      "min-width": header.width ? header.width : "auto",
    };
  }

  addCompany() {
    this.CompanyDataModal = true;
    this.$emit("add-company");
  }

  onEditEvent(companyId: number): void {
    this.selectedCompany = this.companies.find(c => c.id === companyId) || null;
    this.CompanyDataModal = true;
    this.$emit("edit-company", companyId);
  }

  async onDeleteEvent(companyId: number): Promise<void> {
    const companyName = this.companies.find(c => c.id === companyId)?.name || "";
    const confirmed = await this.confirm.open("Delete company?", `Are you sure you want to permanently delete the company '${companyName}'?`);
    if (!confirmed) return;
    try {
      await CompaniesClient.removeById(companyId);
      await Companies.refreshCompanies();
      await this.initializeTable();
      Snackbar.showSnackbar({ text: `Company ${companyName} has been deleted`, color: "success" });
    } catch (error) {
      Snackbar.showSnackbar({ text: "Failed to delete company" });
    }
    this.$emit("delete-company", companyId);
  }

  onViewAsCompanyEvent(companyId: number): void {
    this.selectedCompany = this.companies.find(c => c.id === companyId) || null;
    this.$emit("view-as-company", this.selectedCompany);
    Users.viewAsCompany(companyId).then(() => {
      window.location.reload();
    });
  }

  // @Hooks
  async created(): Promise<void> {
    await this.initializeTable();
  }
}
